import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { SEVERITY_BADGE_CLASS } from '@/lib/severity';
import type { AlertDetailResponse, Severity } from '@/types/alerts';

/**
 * Tiny shared helpers for per-rule evidence renderers (PR 3).
 *
 * Kept deliberately small — only primitives that 3+ renderers need.
 * If you find yourself adding renderer-specific logic here, push it
 * back into the renderer instead.
 */

/**
 * Format a number as South African Rand. Returns "—" for null/undefined.
 * Uses en-ZA locale + R prefix to match how amounts render elsewhere
 * in the SPA (StatCard money formatting, FraudCenter export).
 */
export function formatRand(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `R${new Intl.NumberFormat('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}`;
}

/**
 * Format an integer with thousands separators (en-ZA). Used for km values
 * in the odometer renderer where decimals add noise.
 */
export function formatInt(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-ZA').format(Math.round(value));
}

/**
 * Format an ISO datetime string as a short local-friendly stamp.
 * Returns the raw string on parse failure so we never show "Invalid Date".
 */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * One labelled row in the evidence breakdown. Two columns: dim label,
 * value (mono for numbers/refs, normal for prose).
 */
export function EvidenceRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/40 py-1.5 last:border-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-right text-sm">{children}</span>
    </div>
  );
}

/**
 * Plain-English headline at the top of every renderer. Bolded so the
 * investigator reads it first.
 */
export function HeadlineLine({ children }: { children: ReactNode }): JSX.Element {
  return <p className="text-sm font-semibold">{children}</p>;
}

/**
 * Optional one-liner that re-states the alert's severity tier with a
 * coloured badge. Renderers call this when severity adds context (e.g.
 * "this fired Critical because rollback_km > 50,000"). Skip when the
 * severity is the default for the rule.
 */
export function SeverityContext({
  alert,
  reason,
}: {
  alert: AlertDetailResponse;
  reason: string;
}): JSX.Element {
  const sev: Severity = alert.severity;
  return (
    <p className="text-xs text-muted-foreground">
      Severity{' '}
      <Badge variant="outline" className={SEVERITY_BADGE_CLASS[sev]}>
        {sev}
      </Badge>{' '}
      — {reason}
    </p>
  );
}

/**
 * Wrapper that gives every renderer the same outer spacing + ARIA shape.
 * Children are stacked: headline → evidence rows → optional severity.
 */
export function RendererShell({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  return <div className="flex flex-col gap-3">{children}</div>;
}

/**
 * Null-safe field reader. Pulls a key off the evidence dict and casts to
 * the expected primitive shape, returning undefined on miss. Avoids the
 * "evidence['x'] as number" boilerplate sprinkled across every renderer.
 */
export function getNum(
  evidence: Record<string, unknown> | null | undefined,
  key: string,
): number | undefined {
  const v = evidence?.[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return undefined;
}

export function getStr(
  evidence: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined {
  const v = evidence?.[key];
  if (typeof v === 'string' && v.length > 0) return v;
  return undefined;
}

export function getBool(
  evidence: Record<string, unknown> | null | undefined,
  key: string,
): boolean | undefined {
  const v = evidence?.[key];
  if (typeof v === 'boolean') return v;
  return undefined;
}

/**
 * Fallback line shown when a critical evidence key is missing. Renderers
 * use this instead of crashing or showing "undefined" to the investigator.
 */
export function MissingContextLine({
  message = 'Some context for this alert is unavailable — see raw evidence below.',
}: {
  message?: string;
}): JSX.Element {
  return (
    <p className="text-xs italic text-muted-foreground">{message}</p>
  );
}
